import React, { useState } from "react";
import { useForm, useFormState } from "react-final-form";
import { useDataProvider, useNotify, useRedirect, Button } from "react-admin";
import SaveIcon from "@material-ui/icons/Save";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
} from "@material-ui/core";
import moment from "moment";
import { delay, GetParam } from "utils/tools";

const SaveButton = () => {
  const form = useForm();
  const formState = useFormState();
  const provider = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();
  const param = GetParam();

  const [modalOpen, setModalOpen] = useState(false);
  const [results, setResults] = useState<
    {
      date: string;
      status: "pending" | "success" | "error";
      message?: string;
    }[]
  >([]);
  const [isSaving, setIsSaving] = useState(false);
  const isDisabled = isSaving || formState.invalid || !formState.valid;

  const handleClick = async () => {
    const values = form.getState().values;

    // Generate date range
    const dates: string[] = [];
    for (
      let i = moment(values.fromDate);
      i.isSameOrBefore(values.toDate);
      i.add(1, "day")
    ) {
      dates.push(i.format("YYYY-MM-DD"));
    }

    if (dates.length === 0) {
      notify("No dates to process", "warning");
      return;
    }

    // Initialize results as pending
    const initialResults = dates.map((date) => ({
      date,
      status: "pending" as const,
    }));
    setResults(initialResults);
    setModalOpen(true);
    setIsSaving(true);

    // Process sequentially
    for (let i = 0; i < dates.length; i++) {
      const date = dates[i];
      const { fromDate, toDate, ...restValues } = values;
      const holidayData = { ...restValues, date };

      try {
        await provider.create("holidays", {
          data: holidayData,
        });
        // Update status to success
        setResults((prev) =>
          prev.map((item) =>
            item.date === date ? { ...item, status: "success" } : item,
          ),
        );
      } catch (err: any) {
        // Update status to error
        setResults((prev) =>
          prev.map((item) =>
            item.date === date
              ? {
                  ...item,
                  status: "error",
                  message: err.message || "Unknown error",
                }
              : item,
          ),
        );
      }

      // Skip delay after the last item
      if (i < dates.length - 1) {
        await delay(300);
      }
    }

    setIsSaving(false);
  };

  return (
    <>
      <Button
        label={isSaving ? `Loading...` : `Save`}
        onClick={handleClick}
        disabled={isDisabled}
        startIcon={<SaveIcon />}
      />

      <Dialog
        open={modalOpen}
        maxWidth="sm"
        fullWidth
        onClose={() => !isSaving && setModalOpen(false)}
      >
        <DialogTitle>Holiday Creation Results</DialogTitle>
        <DialogContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>{item.date}</TableCell>
                  <TableCell>
                    {item.status === "pending" && (
                      <>
                        <CircularProgress size={16} /> Pending
                      </>
                    )}
                    {item.status === "success" && (
                      <span style={{ color: "green" }}>Success</span>
                    )}
                    {item.status === "error" && (
                      <span style={{ color: "red" }}>
                        Failed
                        {item.message && `: ${item.message}`}
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setModalOpen(false);
              const success = results.filter(
                (r) => r.status === "success",
              ).length;
              const failed = results.filter((r) => r.status === "error").length;
              if (failed === 0) {
                notify(`✅ All ${success} Holidays created successfully`);
              } else if (success > 0) {
                notify(`⚠️ ${success} succeeded, ${failed} failed`);
              } else {
                notify("❌ All creations failed", "error");
              }
              redirect(
                `/academicYears/${param.get("academicYearId")}/show/2?schoolId=${param.get("schoolId")}`,
              );
            }}
            disabled={isSaving}
            label="Close"
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SaveButton;
